/**
 * One-shot budget for the splash screen's AUTOMATIC self-recovery — a hard
 * navigation to the clean root URL, the same action the manual "Reload app"
 * button performs (clear the signin-redirect budget + `window.location.assign`).
 *
 * Why a budget is mandatory: the recovery resets {@link clearSigninRedirectAttempts}
 * (AuthController's cross-page-load loop guard) so auth can retry — without a
 * cap, a persistently-wedged boot would auto-reload on a machine-speed loop,
 * clearing that guard on every pass. Capping AUTOMATIC recoveries at one per
 * 60s window means a transient wedge (e.g. stale failed-callback `?error/?state`
 * params) self-heals on a single clean reload, while a genuine persistent wedge
 * attempts recovery exactly once and then falls through to the manual button.
 *
 * Deliberately mirrors {@link signinRedirectAttempts} (same sessionStorage +
 * sliding-window shape) but is a SEPARATE budget with its own key and a cap of
 * 1: the two concerns (auth-redirect attempts vs. splash auto-reloads) must not
 * share a counter. sessionStorage (not localStorage) so the budget resets on a
 * fresh tab / manual reload. If storage is unavailable (private mode /
 * partitioned), reads return "no attempts" so the one automatic recovery can
 * still fire — its navigation is one-shot per page load and the manual button
 * remains the ultimate backstop.
 */

const STORAGE_KEY = "boot.splashAutoRecoverAttempts";
const ATTEMPT_WINDOW_MS = 60_000;
const MAX_ATTEMPTS_PER_WINDOW = 1;

function readRecentAttempts(now: number): number[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (ts): ts is number =>
        typeof ts === "number" && now - ts < ATTEMPT_WINDOW_MS
    );
  } catch {
    // Storage unavailable or corrupted — treat as no attempts so the single
    // automatic recovery can still fire once.
    return [];
  }
}

/**
 * True once the splash has already auto-recovered within the current window —
 * i.e. an automatic clean reload ran and we are still (or again) on the splash,
 * which means recovery did not help and this is a genuine persistent wedge.
 */
export function isSplashAutoRecoverExhausted(
  now: number = Date.now()
): boolean {
  return readRecentAttempts(now).length >= MAX_ATTEMPTS_PER_WINDOW;
}

/** Record that an automatic splash recovery was just initiated. */
export function recordSplashAutoRecoverAttempt(now: number = Date.now()): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify([...readRecentAttempts(now), now])
    );
  } catch {
    // Storage unavailable — the per-page-load navigation is still one-shot and
    // the manual "Reload app" button is the backstop, so there is nothing to
    // persist.
  }
}

/** Reset the budget — call on a successful boot / explicit user retry. */
export function clearSplashAutoRecoverAttempts(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
